import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

// ── Replace with your actual wallet addresses ─────────────────────────────
const BITCOIN_ADDRESS =
  "bc1qmyyga2zt77ck6u609n3lcs9fsd7nfp7s9cnaq5";
const MONERO_ADDRESS =
  "88TBiH Qo15yG Yd6hrq xxpFh3 wA29Ty jXf6rr KaRePG 5p7BDR tBAbwM s8MY4y hs5uQ2 GRwhSJ 65Zrp1 NjNVHM 1RxC2s NpQpT";

// ── Replace with your actual QR code images in /public/ ──────────────────
const BTC_QR_URL = "/qr-bitcoin-placeholder.png";
const XMR_QR_URL = "/qr-monero-placeholder.png";

// ── Stripe Payment Link ───────────────────────────────────────────────────
const STRIPE_PAYMENT_LINK =
  "https://buy.stripe.com/eVqfZiaYjfZj4xSeB7gnK00";

const CREDIT_AMOUNTS = [5, 10, 15, 20, 25, 50];

export default function AddCredits() {
  const { user, loading, isQRSession } = useAuth();
  const navigate = useNavigate();

  const [method, setMethod]       = useState("card");
  const [amount, setAmount]       = useState(10);
  const [txHash, setTxHash]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && isQRSession) navigate("/order");
  }, [user, loading, isQRSession, navigate]);

  if (loading || !user) return null;

  // ── Stripe Payment Link redirect ─────────────────────────────────────────
  const handleStripeRedirect = () => {
    const url =
      `${STRIPE_PAYMENT_LINK}?client_reference_id=${user.accountNumber}`;
    window.location.href = url;
  };

  // ── Cash / crypto submission ──────────────────────────────────────────────
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (["bitcoin", "monero"].includes(method) && !txHash.trim()) {
      return setError(
        "Transaction hash/ID is required for crypto payments."
      );
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/credits/request", {
        method,
        amount,
        txHash: txHash.trim() || undefined,
      });
      setResult(data.message);
    } catch (err) {
      setError(err.response?.data?.error || "Request failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
                      text-center border border-farm-tan/30
                      dark:border-gray-700">
        <div className="text-5xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-farm-green
                       dark:text-green-400 mb-3">
          Request Received
        </h2>
        <div className="bg-amber-50 dark:bg-amber-900/30 border
                        border-amber-300 dark:border-amber-600 rounded-xl
                        p-4 text-amber-800 dark:text-amber-300 text-sm mb-6">
          ⏳ {result}
        </div>
        <button
          onClick={() => {
            setResult(null);
            setTxHash("");
          }}
          className="bg-farm-green text-white px-6 py-2 rounded-xl
                     hover:bg-green-700 transition-colors"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
                    border border-farm-tan/30 dark:border-gray-700
                    transition-colors duration-200">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-farm-green
                       dark:text-green-400">
          Add Credits
        </h2>
        <div className="text-right">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Current Balance
          </div>
          <div className="text-lg font-bold text-farm-green
                          dark:text-green-400">
            {user.balance?.toFixed(2)} credits
          </div>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300
                        dark:border-red-600 text-red-700 dark:text-red-300
                        rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* ── Crypto bonus banner — shown when bitcoin or monero selected ── */}
      {(method === "bitcoin" || method === "monero") && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50
                        dark:from-amber-900/30 dark:to-yellow-900/30
                        border border-amber-300 dark:border-amber-600
                        rounded-xl p-4 mb-5 flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">🎁</span>
          <div>
            <p className="font-bold text-amber-800 dark:text-amber-300
                          text-sm">
              Earn a 10% Credit Bonus with Crypto!
            </p>
            <p className="text-amber-700 dark:text-amber-400 text-xs mt-1
                          leading-relaxed">
              When you fund your account with{" "}
              <strong>Bitcoin or Monero</strong>, we add an extra{" "}
              <strong>10% in free credits</strong> — our thanks for
              supporting decentralized payments. For example, a USD 20.00
              payment earns you <strong>22 credits</strong> instead of 20.
              Credits are added manually after payment is verified and may
              take 24–48 hours to appear.
            </p>
          </div>
        </div>
      )}

      {/* ── Payment method selector ── */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700
                          dark:text-gray-300 mb-3">
          Payment Method
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: "card",    label: "💳 Credit / Debit Card" },
            { id: "cash",    label: "💵 Cash (In Person)" },
            { id: "bitcoin", label: "₿ Bitcoin (+10% bonus!)" },
            { id: "monero",  label: "🔒 Monero (+10% bonus!)" },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                setMethod(m.id);
                setError("");
                setTxHash("");
              }}
              className={`
                p-3 rounded-xl border-2 text-sm font-medium transition-all
                text-left
                ${method === m.id
                  ? "border-farm-green bg-green-50 text-farm-green " +
                    "dark:border-green-500 dark:bg-green-900/30 " +
                    "dark:text-green-400"
                  : "border-gray-200 dark:border-gray-600 " +
                    "hover:border-farm-tan dark:hover:border-farm-tan " +
                    "text-gray-700 dark:text-gray-300 " +
                    "bg-white dark:bg-gray-700"
                }
              `}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Amount selector — hidden for card ── */}
      {method !== "card" && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-3">
            Credit Amount{" "}
            <span className="text-gray-400 dark:text-gray-500 font-normal">
              (1 credit = USD 1.00
              {["bitcoin", "monero"].includes(method)
                ? " · +10% bonus applied by us"
                : ""}
              )
            </span>
          </label>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {CREDIT_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAmount(a)}
                className={`
                  p-2 rounded-lg border-2 text-sm font-semibold
                  transition-all
                  ${amount === a
                    ? "border-farm-green bg-green-50 text-farm-green " +
                      "dark:border-green-500 dark:bg-green-900/30 " +
                      "dark:text-green-400"
                    : "border-gray-200 dark:border-gray-600 " +
                      "hover:border-farm-tan " +
                      "text-gray-700 dark:text-gray-300 " +
                      "bg-white dark:bg-gray-700"
                  }
                `}
              >
                {a} credits
                {["bitcoin", "monero"].includes(method) && (
                  <span className="block text-xs text-amber-600
                                   dark:text-amber-400 font-normal">
                    → {(a * 1.1).toFixed(1)} added
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 dark:text-gray-400
                              whitespace-nowrap">
              Custom:
            </label>
            <input
              type="number"
              min={1}
              max={500}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="flex-1 border border-gray-300 dark:border-gray-600
                         rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700
                         text-gray-800 dark:text-gray-100
                         focus:outline-none focus:ring-2
                         focus:ring-farm-green text-sm"
            />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* Method-specific panels                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}

      {/* ── Card ── */}
      {method === "card" && (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 border
                          border-blue-200 dark:border-blue-700 rounded-xl
                          p-4 text-sm text-blue-800 dark:text-blue-300">
            💳 You'll be redirected to our secure Stripe checkout page.
            Select the amount you'd like to add and complete your payment
            there. Credits are added to your account automatically once
            payment is confirmed.
          </div>
          <button
            onClick={handleStripeRedirect}
            className="w-full bg-farm-green text-white py-3 rounded-xl
                       font-semibold hover:bg-green-700 transition-colors"
          >
            Continue to Secure Checkout →
          </button>
        </div>
      )}

      {/* ── Cash ── */}
      {method === "cash" && (
        <form onSubmit={handleManualSubmit} className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/30 border
                          border-amber-300 dark:border-amber-600 rounded-xl
                          p-4 text-sm text-amber-800 dark:text-amber-300">
            💵 Please bring cash to your next pickup or arranged meeting.
            Once we receive your payment, we'll add{" "}
            <strong>{amount} credits</strong> to your account.
            <br />
            <br />
            ⏳{" "}
            <strong>Credits may take 24–48 hours to appear</strong> after
            your cash payment is received.
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-farm-green text-white py-3 rounded-xl
                       font-semibold hover:bg-green-700 transition-colors
                       disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit Cash Credit Request"}
          </button>
        </form>
      )}

      {/* ── Bitcoin ── */}
      {method === "bitcoin" && (
        <CryptoForm
          coin="Bitcoin"
          address={BITCOIN_ADDRESS}
          qrUrl={BTC_QR_URL}
          amount={amount}
          txHash={txHash}
          setTxHash={setTxHash}
          submitting={submitting}
          onSubmit={handleManualSubmit}
        />
      )}

      {/* ── Monero ── */}
      {method === "monero" && (
        <CryptoForm
          coin="Monero"
          address={MONERO_ADDRESS}
          qrUrl={XMR_QR_URL}
          amount={amount}
          txHash={txHash}
          setTxHash={setTxHash}
          submitting={submitting}
          onSubmit={handleManualSubmit}
        />
      )}
    </div>
  );
}

// ── CryptoForm ────────────────────────────────────────────────────────────
function CryptoForm({
  coin,
  address,
  qrUrl,
  amount,
  txHash,
  setTxHash,
  submitting,
  onSubmit,
}) {
  const bonusAmount = (amount * 1.1).toFixed(1);
  const isBitcoin   = coin === "Bitcoin";

  return (
    <form onSubmit={onSubmit} className="space-y-4">

      {/* Info banner */}
      <div className="bg-purple-50 dark:bg-purple-900/30 border
                      border-purple-200 dark:border-purple-700 rounded-xl
                      p-4 text-sm text-purple-800 dark:text-purple-300">
        🔒 Send <strong>USD {amount}.00 equivalent</strong> in {coin} to
        the address below. After sending, paste your transaction ID/hash
        in the field below so we can verify your payment.
        <br />
        <br />
        🎁{" "}
        <strong>You will receive {bonusAmount} credits</strong> — that's
        your {amount} credits plus a <strong>10% bonus</strong>{" "}
        ({(amount * 0.1).toFixed(1)} free credits) for paying with {coin}!
        <br />
        <br />
        ⏳{" "}
        <strong>Credits may take 24–48 hours to appear</strong> after the
        transaction is confirmed on the network.
      </div>

      {/* QR code + address block */}
      <div className="flex flex-col items-center gap-3 p-4 bg-gray-50
                      dark:bg-gray-700/50 rounded-xl border border-gray-200
                      dark:border-gray-600">

        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {coin} Wallet QR Code
        </div>

        {/* QR image */}
        <img
          src={qrUrl}
          alt={`${coin} QR code`}
          className="h-40 w-40 object-contain border border-gray-200
                     dark:border-gray-600 rounded-lg bg-white p-2"
          onError={(e) => {
            e.target.style.display = "none";
            const fb = document.getElementById(`qr-fb-${coin}`);
            if (fb) fb.style.display = "flex";
          }}
        />

        {/* Fallback placeholder when image is missing */}
        <div
          id={`qr-fb-${coin}`}
          style={{ display: "none" }}
          className="h-40 w-40 border-2 border-dashed border-gray-300
                     dark:border-gray-600 rounded-lg items-center
                     justify-center text-center text-xs text-gray-400 p-2"
        >
          Add your {coin} QR image to /public/
        </div>

        {/* On-chain address */}
        <div className="text-xs font-mono break-all text-center text-gray-600
                        dark:text-gray-300 bg-white dark:bg-gray-700 border
                        border-gray-200 dark:border-gray-600 rounded-lg p-2
                        w-full">
          {address}
        </div>
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(address)}
          className="text-xs text-farm-green dark:text-green-400
                     hover:underline"
        >
          Copy address
        </button>

        {/* Lightning panel — Bitcoin only */}
        {isBitcoin && (
          <div className="w-full border-t border-gray-200
                          dark:border-gray-600 pt-3 mt-1">
            <p className="text-xs text-center text-gray-500
                          dark:text-gray-400 mb-1.5">
              ⚡ Prefer Lightning Network payments?
            </p>
            <div className="flex items-center justify-between
                            bg-yellow-50 dark:bg-yellow-900/30 border
                            border-yellow-200 dark:border-yellow-700
                            rounded-lg px-3 py-2">
              <span className="text-sm font-mono font-semibold
                               text-yellow-800 dark:text-yellow-300">
                homestead@cake.cash
              </span>
              <button
                type="button"
                onClick={() =>
                  navigator.clipboard.writeText("homestead@cake.cash")
                }
                className="text-xs text-yellow-700 dark:text-yellow-400
                           hover:underline ml-3 whitespace-nowrap"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-center text-gray-400
                          dark:text-gray-500 mt-1.5 leading-relaxed">
              Send Lightning payments to the address above, then paste
              your payment hash in the field below.
            </p>
          </div>
        )}
      </div>

      {/* Transaction hash input */}
      <div>
        <label className="block text-sm font-medium text-gray-700
                          dark:text-gray-300 mb-1">
          Transaction ID / Hash{" "}
          <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={txHash}
          onChange={(e) => setTxHash(e.target.value)}
          placeholder={
            isBitcoin
              ? "On-chain TX ID or Lightning payment hash"
              : `Paste your ${coin} transaction ID here`
          }
          className="w-full border border-gray-300 dark:border-gray-600
                     rounded-lg px-3 py-2 font-mono text-sm
                     bg-white dark:bg-gray-700 text-gray-800
                     dark:text-gray-100 placeholder-gray-400
                     dark:placeholder-gray-500 focus:outline-none
                     focus:ring-2 focus:ring-farm-green"
        />
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Required — this allows us to verify your payment.
        </p>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitting || !txHash.trim()}
        className="w-full bg-farm-green text-white py-3 rounded-xl
                   font-semibold hover:bg-green-700 transition-colors
                   disabled:opacity-60"
      >
        {submitting
          ? "Submitting…"
          : `Submit ${coin} Payment Request`}
      </button>
    </form>
  );
}
